import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Alert,
  Chip,
  Grid,
  Autocomplete,
  Card,
  CardContent,
  Divider,
  Tooltip,
  TablePagination,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DataGrid } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Check as FinalizeIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import api from '../services/api';

const ProductInventoryClosing = () => {
  const [closings, setClosings] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [finalizeDialogOpen, setFinalizeDialogOpen] = useState(false);
  const [saveAndFinalizeDialogOpen, setSaveAndFinalizeDialogOpen] = useState(false);
  const [editingClosing, setEditingClosing] = useState(null);
  const [viewingClosing, setViewingClosing] = useState(null);
  const [deletingClosing, setDeletingClosing] = useState(null);
  const [finalizingClosing, setFinalizingClosing] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Pagination and filtering
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    status: '',
    from: null,
    to: null,
  });
  const [showFilters, setShowFilters] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    closing_date: new Date(),
    remarks: '',
    details: [],
  });

  // Fetch closings with pagination and filters
  const fetchClosings = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page + 1,
        limit: rowsPerPage,
      });
      
      if (filters.status) params.append('status', filters.status);
      if (filters.from) params.append('from', format(filters.from, 'yyyy-MM-dd'));
      if (filters.to) params.append('to', format(filters.to, 'yyyy-MM-dd'));

      const response = await api.get(`/api/v1/product-inventory-closing?${params}`);
      setClosings(response.data.data || []);
    } catch (error) {
      console.error('Error fetching closings:', error);
      setError('Failed to load inventory closings');
    } finally {
      setLoading(false);
    }
  };

  // Fetch products for details
  const fetchProducts = async () => {
    try {
      const response = await api.get('/api/v1/products');
      setProducts(response.data.data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  // Prepare inventory data for new closing
  const prepareInventoryData = async () => {
    try {
      const response = await api.get('/api/v1/product-inventory-closing/prepare/data');
      return response.data.data || [];
    } catch (error) {
      console.error('Error preparing inventory data:', error);
      setError('Failed to prepare inventory data');
      return [];
    }
  };

  useEffect(() => {
    fetchClosings();
    fetchProducts();
  }, [page, rowsPerPage, filters]);

  const handleOpenDialog = async (closing = null) => {
    setEditingClosing(closing);
    if (closing) {
      // Edit mode - fetch full details
      try {
        const response = await api.get(`/api/v1/product-inventory-closing/${closing.id}`);
        const closingData = response.data.data;
        setFormData({
          closing_date: new Date(closingData.closing_date),
          remarks: closingData.remarks || '',
          details: closingData.details || [],
        });
      } catch (error) {
        setError('Failed to load closing details');
        return;
      }
    } else {
      // Create mode - prepare fresh data
      const inventoryData = await prepareInventoryData();
      setFormData({
        closing_date: new Date(),
        remarks: '',
        details: inventoryData,
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingClosing(null);
    setFormData({
      closing_date: new Date(),
      remarks: '',
      details: [],
    });
    setError('');
    setSuccess('');
  };

  const handleSaveAndFinalize = () => {
    // Validate that all physical quantities are filled
    const incompleteItems = formData.details.filter(detail => 
      detail.physical_qty === undefined || detail.physical_qty === '' || detail.physical_qty === null
    );
    
    if (incompleteItems.length > 0) {
      setError('Please fill in all physical quantities before finalizing the inventory closing.');
      return;
    }
    
    setSaveAndFinalizeDialogOpen(true);
  };

  const confirmSaveAndFinalize = async () => {
    setSaveAndFinalizeDialogOpen(false);
    await handleSave(true);
  };

  const handleSave = async (shouldFinalize = false) => {
    try {
      const payload = {
        closing_date: format(formData.closing_date, 'yyyy-MM-dd'),
        remarks: formData.remarks,
        details: formData.details.map(detail => ({
          product_id: detail.product_id,
          system_qty: parseFloat(detail.system_qty || 0),
          physical_qty: parseFloat(detail.physical_qty || 0),
          cost_per_unit: parseFloat(detail.cost_per_unit || 0),
        })),
      };

      let closingId;
      if (editingClosing) {
        await api.put(`/api/v1/product-inventory-closing/${editingClosing.id}`, payload);
        closingId = editingClosing.id;
        setSuccess(`Inventory closing ${shouldFinalize ? 'updated and finalized' : 'updated'} successfully`);
      } else {
        const response = await api.post('/api/v1/product-inventory-closing', payload);
        closingId = response.data.data.id;
        setSuccess(`Inventory closing ${shouldFinalize ? 'created and finalized' : 'created'} successfully`);
      }

      // If shouldFinalize is true, finalize the closing
      if (shouldFinalize) {
        await api.put(`/api/v1/product-inventory-closing/${closingId}/finalize`);
      }

      handleCloseDialog();
      fetchClosings();
    } catch (error) {
      console.error('Error saving closing:', error);
      setError(error.response?.data?.message || 'Failed to save inventory closing');
    }
  };

  const handleView = async (closing) => {
    try {
      const response = await api.get(`/api/v1/product-inventory-closing/${closing.id}`);
      setViewingClosing(response.data.data);
      setViewDialogOpen(true);
    } catch (error) {
      setError('Failed to load closing details');
    }
  };

  const handleDelete = (closing) => {
    setDeletingClosing(closing);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/api/v1/product-inventory-closing/${deletingClosing.id}`);
      setSuccess('Inventory closing deleted successfully');
      setDeleteDialogOpen(false);
      setDeletingClosing(null);
      fetchClosings();
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to delete inventory closing');
    }
  };

  const handleFinalize = (closing) => {
    setFinalizingClosing(closing);
    setFinalizeDialogOpen(true);
  };

  const confirmFinalize = async () => {
    try {
      await api.put(`/api/v1/product-inventory-closing/${finalizingClosing.id}/finalize`);
      setSuccess('Inventory closing finalized successfully');
      setFinalizeDialogOpen(false);
      setFinalizingClosing(null);
      fetchClosings();
      if (viewingClosing?.id === finalizingClosing.id) {
        setViewingClosing({ ...viewingClosing, status: 'finalized' });
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to finalize inventory closing');
    }
  };

  const handleDetailChange = (index, field, value) => {
    const updatedDetails = [...formData.details];
    updatedDetails[index][field] = value;
    
    // Auto-calculate variance and total value
    if (field === 'physical_qty' || field === 'system_qty') {
      const physical = parseFloat(updatedDetails[index].physical_qty || 0);
      const system = parseFloat(updatedDetails[index].system_qty || 0);
      updatedDetails[index].variance_qty = physical - system;
      updatedDetails[index].total_value = physical * parseFloat(updatedDetails[index].cost_per_unit || 0);
    }
    
    if (field === 'cost_per_unit') {
      const physical = parseFloat(updatedDetails[index].physical_qty || 0);
      updatedDetails[index].total_value = physical * parseFloat(value || 0);
    }
    
    setFormData({ ...formData, details: updatedDetails });
  };

  const handleFilterChange = (field, value) => {
    setFilters({ ...filters, [field]: value });
    setPage(0); // Reset to first page when filtering
  };

  const clearFilters = () => {
    setFilters({ status: '', from: null, to: null });
    setPage(0);
  };

  const getStatusChip = (status) => {
    return (
      <Chip
        label={status.charAt(0).toUpperCase() + status.slice(1)}
        color={status === 'finalized' ? 'success' : 'default'}
        size="small"
      />
    );
  };

  const getVarianceDisplay = (variance) => {
    if (!variance || variance === 0) return '0';
    return (
      <span style={{ color: variance > 0 ? 'green' : 'red', fontWeight: 'bold' }}>
        {variance > 0 ? '+' : ''}{variance}
      </span>
    );
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'PHP'
    }).format(value || 0);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Product Inventory Closing
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {/* Filters */}
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: showFilters ? 2 : 0 }}>
              <Button
                startIcon={<FilterIcon />}
                onClick={() => setShowFilters(!showFilters)}
                variant="outlined"
                size="small"
              >
                Filters
              </Button>
              {(filters.status || filters.from || filters.to) && (
                <Button
                  startIcon={<ClearIcon />}
                  onClick={clearFilters}
                  size="small"
                >
                  Clear
                </Button>
              )}
            </Box>
            
            {showFilters && (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={filters.status}
                      label="Status"
                      onChange={(e) => handleFilterChange('status', e.target.value)}
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="draft">Draft</MenuItem>
                      <MenuItem value="finalized">Finalized</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <DatePicker
                    label="From Date"
                    value={filters.from}
                    onChange={(date) => handleFilterChange('from', date)}
                    renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <DatePicker
                    label="To Date"
                    value={filters.to}
                    onChange={(date) => handleFilterChange('to', date)}
                    renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                  />
                </Grid>
              </Grid>
            )}
          </CardContent>
        </Card>

        {/* Main Table */}
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Closing Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created By</TableCell>
                  <TableCell>Remarks</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">Loading...</TableCell>
                  </TableRow>
                ) : closings.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">No inventory closings found</TableCell>
                  </TableRow>
                ) : (
                  closings.map((closing) => (
                    <TableRow key={closing.id} hover>
                      <TableCell>
                        {format(new Date(closing.closing_date), 'MMM dd, yyyy')}
                      </TableCell>
                      <TableCell>{getStatusChip(closing.status)}</TableCell>
                      <TableCell>{closing.created_by_name || closing.created_by}</TableCell>
                      <TableCell>{closing.remarks || '-'}</TableCell>
                      <TableCell>
                        <Tooltip title="View Details">
                          <IconButton size="small" onClick={() => handleView(closing)}>
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        {closing.status === 'draft' && (
                          <>
                            <Tooltip title="Edit">
                              <IconButton size="small" onClick={() => handleOpenDialog(closing)}>
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton size="small" onClick={() => handleDelete(closing)}>
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={-1} // We'll implement this when we have total count from API
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
          />
        </Paper>

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="lg" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {editingClosing ? 'Edit Inventory Closing' : 'Create New Inventory Closing'}
              {editingClosing && (
                <Chip 
                  label={editingClosing.status} 
                  color={editingClosing.status === 'finalized' ? 'success' : 'default'}
                  size="small"
                />
              )}
            </Box>
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="Closing Date"
                  value={formData.closing_date}
                  onChange={(date) => setFormData({ ...formData, closing_date: date })}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Status"
                  value="Draft"
                  disabled
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Remarks"
                  value={formData.remarks}
                  onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                  multiline
                  rows={3}
                  fullWidth
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>
              Product Details
            </Typography>

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Product</TableCell>
                    <TableCell>SKU</TableCell>
                    <TableCell>System Qty</TableCell>
                    <TableCell>Physical Qty</TableCell>
                    <TableCell>Variance</TableCell>
                    <TableCell>Cost/Unit</TableCell>
                    <TableCell>Total Value</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formData.details.map((detail, index) => (
                    <TableRow key={detail.product_id || index}>
                      <TableCell>{detail.product_name || detail.name}</TableCell>
                      <TableCell>{detail.sku}</TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          value={detail.system_qty || 0}
                          onChange={(e) => handleDetailChange(index, 'system_qty', e.target.value)}
                          size="small"
                          inputProps={{ step: 0.01 }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          value={detail.physical_qty || 0}
                          onChange={(e) => handleDetailChange(index, 'physical_qty', e.target.value)}
                          size="small"
                          inputProps={{ step: 0.01 }}
                        />
                      </TableCell>
                      <TableCell>
                        {getVarianceDisplay(detail.variance_qty)}
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          value={detail.cost_per_unit || 0}
                          onChange={(e) => handleDetailChange(index, 'cost_per_unit', e.target.value)}
                          size="small"
                          inputProps={{ step: 0.01 }}
                        />
                      </TableCell>
                      <TableCell>
                        {formatCurrency(detail.total_value)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button onClick={() => handleSave(false)} variant="outlined">
              Save Draft
            </Button>
            {(!editingClosing || editingClosing.status === 'draft') && (
              <Button 
                onClick={handleSaveAndFinalize} 
                variant="contained" 
                color="success"
                startIcon={<FinalizeIcon />}
              >
                Save & Finalize
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* View Dialog */}
        <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="lg" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Inventory Closing Details</span>
              {viewingClosing?.status === 'draft' && (
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<FinalizeIcon />}
                  onClick={() => handleFinalize(viewingClosing)}
                >
                  Finalize Closing
                </Button>
              )}
            </Box>
          </DialogTitle>
          <DialogContent>
            {viewingClosing && (
              <>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="subtitle2">Closing Date</Typography>
                    <Typography>{format(new Date(viewingClosing.closing_date), 'MMM dd, yyyy')}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="subtitle2">Status</Typography>
                    {getStatusChip(viewingClosing.status)}
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="subtitle2">Created By</Typography>
                    <Typography>{viewingClosing.created_by_name || viewingClosing.created_by}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2">Remarks</Typography>
                    <Typography>{viewingClosing.remarks || 'No remarks'}</Typography>
                  </Grid>
                </Grid>

                <Typography variant="h6" gutterBottom>
                  Product Details
                </Typography>

                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Product</TableCell>
                        <TableCell>SKU</TableCell>
                        <TableCell>System Qty</TableCell>
                        <TableCell>Physical Qty</TableCell>
                        <TableCell>Variance</TableCell>
                        <TableCell>Cost/Unit</TableCell>
                        <TableCell>Total Value</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {viewingClosing.details?.map((detail, index) => (
                        <TableRow key={detail.id || index}>
                          <TableCell>{detail.product_name}</TableCell>
                          <TableCell>{detail.sku}</TableCell>
                          <TableCell>{detail.system_qty}</TableCell>
                          <TableCell>{detail.physical_qty}</TableCell>
                          <TableCell>
                            {getVarianceDisplay(detail.variance_qty)}
                          </TableCell>
                          <TableCell>{formatCurrency(detail.cost_per_unit)}</TableCell>
                          <TableCell>{formatCurrency(detail.total_value)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
            {viewingClosing?.status === 'draft' && (
              <Button
                variant="outlined"
                onClick={() => {
                  setViewDialogOpen(false);
                  handleOpenDialog(viewingClosing);
                }}
              >
                Edit
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this inventory closing? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button onClick={confirmDelete} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Save & Finalize Confirmation Dialog */}
        <Dialog open={saveAndFinalizeDialogOpen} onClose={() => setSaveAndFinalizeDialogOpen(false)}>
          <DialogTitle>Confirm Save & Finalize</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to save and finalize this inventory closing? This will:
            </Typography>
            <ul>
              <li>Save the current draft</li>
              <li>Update product stock quantities</li>
              <li>Create audit records for all adjustments</li>
              <li>Make this closing read-only</li>
            </ul>
            <Typography color="warning.main">
              This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSaveAndFinalizeDialogOpen(false)}>Cancel</Button>
            <Button onClick={confirmSaveAndFinalize} color="success" variant="contained">
              Save & Finalize
            </Button>
          </DialogActions>
        </Dialog>

        {/* Finalize Confirmation Dialog */}
        <Dialog open={finalizeDialogOpen} onClose={() => setFinalizeDialogOpen(false)}>
          <DialogTitle>Confirm Finalize</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to finalize this inventory closing? This will:
            </Typography>
            <ul>
              <li>Update product stock quantities</li>
              <li>Create audit records for all adjustments</li>
              <li>Make this closing read-only</li>
            </ul>
            <Typography color="warning.main">
              This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setFinalizeDialogOpen(false)}>Cancel</Button>
            <Button onClick={confirmFinalize} color="success" variant="contained">
              Finalize
            </Button>
          </DialogActions>
        </Dialog>

        {/* Floating Action Button */}
        <Fab
          color="primary"
          aria-label="add"
          sx={{ position: 'fixed', bottom: 16, right: 16 }}
          onClick={() => handleOpenDialog()}
        >
          <AddIcon />
        </Fab>
      </Box>
    </LocalizationProvider>
  );
};

export default ProductInventoryClosing;
